import { QueryClient, QueryKey } from '@tanstack/react-query';
import Workspace from '../../types/Workspace';
import { WorkspaceView } from '../../types/WorkspaceView';

/**
 * Shared React Query list-cache mutations for socket handlers.
 *
 * Every helper guards an absent cache: React Query invokes the updater with
 * `undefined` when no entry exists, so a bare `old.map`/`old.filter` throws
 * inside the socket event callback. Centralising the guard here removes that
 * crash class and the per-handler duplication.
 */

/** Replace the item with a matching id, or append it to the end. Seeds an absent cache. */
export function upsertToEnd<T extends { id: number | string }>(queryClient: QueryClient, queryKey: QueryKey, item: T) {
  queryClient.setQueryData<T[]>(queryKey, (old) => [...(old ?? []).filter((x) => x.id !== item.id), item]);
}

/** Remove every item whose id is in `ids`. No-op when the cache is absent. */
export function removeListItems<T extends { id: number | string }>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  ids: Array<number | string> | number | string
) {
  const idSet = new Set(Array.isArray(ids) ? ids : [ids]);
  queryClient.setQueryData<T[]>(queryKey, (old) => (old ? old.filter((x) => !idSet.has(x.id)) : old));
}

/**
 * Remove every item whose id is in `ids` from EVERY cache entry under `rootKey`.
 *
 * For list caches that are keyed by a parent the event does not name. A task
 * delete carries the deleted id and nothing else, so the one
 * `taskSubtasks(parentId)` entry holding that row cannot be addressed directly
 * and every entry has to be swept (#3100).
 *
 * Sweeping every entry is safe for unrelated lists: React Query's structural
 * sharing returns the previous reference when the filtered result is deeply
 * equal, so an entry holding none of the ids does not re-render.
 */
export function removeListItemsEverywhere<T extends { id: number | string }>(
  queryClient: QueryClient,
  rootKey: QueryKey,
  ids: Array<number | string> | number | string
) {
  const idSet = new Set(Array.isArray(ids) ? ids : [ids]);
  queryClient.setQueriesData<T[]>({ queryKey: rootKey }, (old) => (old ? old.filter((x) => !idSet.has(x.id)) : old));
}

/** Map each item in place via `fn`. No-op when the cache is absent. */
export function mapListCache<T>(queryClient: QueryClient, queryKey: QueryKey, fn: (item: T) => T) {
  queryClient.setQueryData<T[]>(queryKey, (old) => (old ? old.map(fn) : old));
}

/** Insert an item at the top or bottom of a list cache entry. Seeds an absent cache. */
export function insertIntoList<T>(oldData: T[] | undefined, item: T, addToTop: boolean): T[] {
  const list = oldData ?? [];
  return addToTop ? [item, ...list] : [...list, item];
}

/** Update a workspace's `Views` array via `fn`. No-op when the workspace cache is absent. */
export function updateWorkspaceViews(
  queryClient: QueryClient,
  queryKey: QueryKey,
  fn: (views: WorkspaceView[]) => WorkspaceView[]
) {
  queryClient.setQueryData<Workspace>(queryKey, (old) => (old ? { ...old, Views: fn(old.Views) } : old));
}
